import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

export default function EmployeeTableAuto() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({});

  const formatDateForQuery = (d) => {
    if (!d) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    }
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      return `${String(dt.getDate()).padStart(2, "0")}/${String(
        dt.getMonth() + 1
      ).padStart(2, "0")}/${dt.getFullYear()}`;
    }
    return d;
  };

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const state = qs.get("state") ?? "";
    const state_id = qs.get("state_id") ?? "";
    const employee_id =
      qs.get("employee_id") ?? qs.get("id") ?? qs.get("emp_id") ?? "";
    const division = qs.get("division") ?? "";
    const working_date_raw = qs.get("working_date") ?? "";
    const working_date = working_date_raw
      ? formatDateForQuery(decodeURIComponent(working_date_raw))
      : "";
    const quarter = qs.get("quarter") ?? "";

    const p = {
      state: state || undefined,
      state_id: state_id || undefined,
      employee_id: employee_id || undefined,
      division: division || undefined,
      working_date: working_date || undefined,
      quarter: quarter || undefined,
    };
    setParams(p);

    (async () => {
      setLoading(true);
      try {
        const search = new URLSearchParams();
        if (p.state_id) search.set("state_id", p.state_id);
        if (p.state) search.set("state", p.state);
        if (p.employee_id) search.set("employee_id", p.employee_id);
        if (p.division) search.set("division", p.division);
        if (p.working_date) search.set("working_date", p.working_date);
        if (p.quarter) search.set("quarter", p.quarter);

        const url = `${BASE_URL}/api/pob-targets/pob_target_report?${search.toString()}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });

        const payload = res.data;
        const raw = Array.isArray(payload) ? payload : payload?.data ?? [];

        // UPDATED → removed "bid", added employee_id
        const shaped = raw.map((e, idx) => ({
          sno: idx + 1,
          employee_id: e.employee_id, // REPLACED BID
          code: e.designation ?? "",
          name: e.employee_name ?? "",
          mobile_time: e.last_login_time ?? "",
          latestLevel: e.latest_battery_level ?? "",
          charging: e.charging ?? e.ac_charging ?? e.usb_charging ?? "",
          deviceId: e.device_id ?? "",
          id: e.employee_id ?? "",
          doj: e.final_joining_date ?? "",
          mobile: e.phone_no ?? "",
          raw: e,
        }));

        setEmployees(shaped);
      } catch (err) {
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // No change in detail popup logic
  const fetchEmployeeDetails = async (employee) => {
    const empId = employee.employee_id ?? "";
    if (!empId) return [];
    const url = `${BASE_URL}/api/pob-targets/pob_target_report?employee_id=${encodeURIComponent(
      empId
    )}`;
    const res = await axios.get(url, { headers: getAuthHeaders() });
    const payload = res.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [payload];
  };

  const openEmployeeDetail = async (employee) => {
    let rows;
    try {
      rows = await fetchEmployeeDetails(employee);
    } catch {
      alert("Failed to load details.");
      return;
    }

    const popup = window.open(
      "",
      "_blank",
      "width=1100,height=700,scrollbars=yes"
    );
    if (!popup) {
      alert("Popup blocked.");
      return;
    }

    const style = `
      body{font-family:Arial;margin:10px;}
      table{border-collapse:collapse;width:100%;font-size:13px}
      th,td{border:1px solid #bde6ea;padding:6px;text-align:left}
      thead th{background:#cff0f3;font-weight:700}
      tbody tr{background:#f8d7d7}
    `;

    const keys = rows.length ? Object.keys(rows[0]) : [];
    const headerHtml = keys.map((k) => `<th>${k}</th>`).join("");

    const rowsHtml = rows
      .map(
        (r) =>
          `<tr>${keys.map((k) => `<td>${r[k] ?? ""}</td>`).join("")}</tr>`
      )
      .join("");

    const html = `
      <html><head><style>${style}</style></head>
      <body>
        <h3>Employee Details</h3>
        <table>
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
      </html>`;

    popup.document.write(html);
    popup.document.close();
  };

  return (
    <div className="w-full min-h-screen bg-white p-4">
      <div className="border border-gray-400 p-4">
       <div className="flex items-center justify-center p-2 mb-4">
  <h1 className="flex text-2xl sm:text-3xl font-bold text-gray-800 gap-x-2">
    <span className="text-blue-700">Battery</span>
    <span className="text-blue-500">Stats</span>
  </h1>
</div>


        <div className="overflow-x-auto border border-cyan-400">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-cyan-300">
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Select
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  S.No
                </th>

                {/* UPDATED → "Bid" replaced with Employee ID */}
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Employee ID
                </th>

                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Code
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Employee
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Mobile Time
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Latest Level
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Charging
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Device ID
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  ID
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  DOJ
                </th>
                <th className="border border-cyan-400 px-2 py-1 font-bold">
                  Mobile
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="text-center py-6 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : employees.length ? (
                employees.map((emp) => (
                  <tr
                    key={emp.sno}
                    className="bg-red-200 hover:bg-red-300"
                  >
                    <td className="border border-cyan-400 px-2 py-1 text-center">
                      <input type="checkbox" />
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.sno}
                    </td>

                    {/* UPDATED → show employee_id */}
                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.employee_id}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.code}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openEmployeeDetail(emp);
                        }}
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        {emp.name}
                      </a>
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.mobile_time}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1 text-center">
                      {emp.latestLevel}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.charging}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.deviceId}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.id}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.doj}
                    </td>

                    <td className="border border-cyan-400 px-2 py-1">
                      {emp.mobile}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="text-center py-6 text-gray-500">
                    No records returned from API using current URL params.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
